import React, { useState, useEffect } from "react";
import { Button, Col, DatePicker, Form, Input, Modal, Row, Select, Typography } from "antd";
import { EditOutlined } from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

import { Error, Success } from "../../../commons/Messages/Messages";
import { isAuthenticated } from "../../../commons/Auth/auth";
import MainButtonComp from "../../../commons/Buttons/MainButtonComp";
import { ALL_STATUSES, firstDate } from "./Tasks.constants";

const { Title } = Typography;
const { TextArea } = Input;

const PRIORITIES = ["High", "Medium", "Low"];

const toId = value => (value && typeof value === "object" ? value._id : value);

const initialValues = (isEdit, item) =>
  isEdit && item
    ? {
        title: item.title,
        leader: toId(item.leader) || undefined,
        type: item.type || "Pending",
        priority: item.priority || "Medium",
        startDate: firstDate(item.startDate) ? dayjs(firstDate(item.startDate)) : undefined,
        description: item.description,
        team: (item.team || []).map(toId).filter(Boolean)
      }
    : { type: "Pending", priority: "Medium", team: [] };

// Add or edit a task. With `open`/`onClose` the parent controls visibility (the ticket workflow
// board); otherwise the component renders its own button or edit icon. `ticketId` links a new
// task to a ticket; `extra` adds other links to a new task (e.g. { project, milestone }).
const TaskDrawer = ({ mode, item, updateParent, ticketId, extra, open, onClose }) => {
  const { t } = useTranslation("dashboard");
  const isEdit = mode === "edit";
  const controlled = open !== undefined;

  const [form] = Form.useForm();
  const [employees, setEmployees] = useState([]);
  const [localOpen, setLocalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const isOpen = controlled ? open : localOpen;

  useEffect(() => {
    if (isOpen && employees.length === 0) fetchEmployees();
  }, [isOpen]);

  const close = () => {
    if (controlled) onClose?.();
    else setLocalOpen(false);
  };

  const fetchEmployees = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/employees/employee/creator/${isAuthenticated()?.serviceProviderId}`,
        { headers: { Authorization: "Bearer " + localStorage.getItem("token") } }
      );
      setEmployees(
        (res.data || []).map(d => ({
          label: [d.firstName, d.lastName].filter(Boolean).join(" ") || d.email,
          value: d._id
        }))
      );
    } catch (err) {
      Error(err.response?.data?.errorMessage || err.message);
    }
  };

  const handleOk = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return; // the form shows what is missing
    }
    const payload = {
      ...values,
      startDate: values.startDate ? values.startDate.format("YYYY-MM-DD") : null
    };
    const headers = { Authorization: "Bearer " + localStorage.getItem("token") };
    setSaving(true);
    try {
      const res = isEdit
        ? await axios.put(
            `${process.env.REACT_APP_BACKEND_URL}/tasks/update/${item?._id}`,
            payload,
            { headers }
          )
        : await axios.post(
            `${process.env.REACT_APP_BACKEND_URL}/tasks/add`,
            {
              ...payload,
              serviceProvider: isAuthenticated()?.serviceProviderId,
              ...(extra || {}),
              ...(ticketId ? { ticketId } : {})
            },
            { headers }
          );
      Success(t(res.data.successMessage));
      updateParent?.();
      close();
    } catch (err) {
      Error(err.response?.data?.errorMessage || err.message);
    } finally {
      setSaving(false);
    }
  };

  const label = text => (
    <Title level={5} className="modals-inputs-label">
      {text}
    </Title>
  );

  const formContent = (
    <Form
      form={form}
      layout="vertical"
      hideRequiredMark
      preserve={false}
      initialValues={initialValues(isEdit, item)}
    >
      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <Form.Item
            name="title"
            label={label(t("Title"))}
            rules={[
              { required: true, whitespace: true, message: t("enter", { field: t("Title") }) }
            ]}
          >
            <Input size="large" placeholder={t("enter", { field: t("Title") })} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            name="leader"
            label={label(t("Leader"))}
            rules={[{ required: true, message: t("select", { field: t("Leader") }) }]}
          >
            <Select
              size="large"
              showSearch
              optionFilterProp="label"
              placeholder={t("select", { field: t("Leader") })}
              options={employees}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <Form.Item
            name="type"
            label={label(t("Status"))}
            rules={[{ required: true, message: t("select", { field: t("Status") }) }]}
          >
            <Select size="large" options={ALL_STATUSES.map(s => ({ value: s, label: t(s) }))} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            name="priority"
            label={label(t("Priority"))}
            rules={[{ required: true, message: t("select", { field: t("Priority") }) }]}
          >
            <Select size="large" options={PRIORITIES.map(p => ({ value: p, label: t(p) }))} />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <Form.Item name="startDate" label={label(t("Date"))}>
            <DatePicker style={{ width: "100%" }} size="large" format="DD/MMM/YYYY" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            name="team"
            label={label(t("Team Members"))}
            rules={[{ required: true, message: t("select", { field: t("Team Members") }) }]}
          >
            <Select
              mode="multiple"
              size="large"
              optionFilterProp="label"
              placeholder={t("select", { field: t("Team Members") })}
              options={employees}
            />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item name="description" label={label(t("Description"))}>
        <TextArea rows={4} placeholder={t("enter", { field: t("Description") })} />
      </Form.Item>
    </Form>
  );

  return (
    <>
      {!controlled &&
        (isEdit ? (
          <Button
            type="text"
            size="small"
            className="desk-card__icon-btn"
            icon={<EditOutlined />}
            aria-label={t("Edit Task")}
            onClick={() => setLocalOpen(true)}
          />
        ) : (
          <MainButtonComp
            text={t("Add Task")}
            addBtn
            type="submit"
            onClick={() => setLocalOpen(true)}
          />
        ))}

      {/* Phones get a full-screen sheet with Save/Cancel pinned (design-system/responsive.css). */}
      <Modal
        title={isEdit ? t("Edit Task") : t("Add Task")}
        open={isOpen}
        okText={t("Save")}
        cancelText={t("Cancel")}
        confirmLoading={saving}
        onOk={handleOk}
        onCancel={close}
        width={700}
        centered
        destroyOnClose
      >
        {formContent}
      </Modal>
    </>
  );
};

export default TaskDrawer;
